"use client";

import * as React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Rating,
} from "@mui/material";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { useI18n } from "i18n/I18nProvider";

type Review = {
  title: string;
  quote: string;
  author: string;
  tag?: string;
};

export default function ReviewsClient({ locale }: { locale: string }) {
  const { t } = useI18n();

  const reviews: Review[] = React.useMemo(
    () => [
      {
        title: t("reviews_card_1_title"),
        quote: t("reviews_card_1_quote"),
        author: t("reviews_card_1_author"),
        tag: t("reviews_tag_families"),
      },
      {
        title: t("reviews_card_2_title"),
        quote: t("reviews_card_2_quote"),
        author: t("reviews_card_2_author"),
        tag: t("reviews_tag_couples"),
      },
      {
        title: t("reviews_card_3_title"),
        quote: t("reviews_card_3_quote"),
        author: t("reviews_card_3_author"),
        tag: t("reviews_tag_groups"),
      },
      {
        title: t("reviews_card_4_title"),
        quote: t("reviews_card_4_quote"),
        author: t("reviews_card_4_author"),
        tag: t("reviews_tag_location"),
      },
      {
        title: t("reviews_card_5_title"),
        quote: t("reviews_card_5_quote"),
        author: t("reviews_card_5_author"),
        tag: t("reviews_tag_cleanliness"),
      },
      {
        title: t("reviews_card_6_title"),
        quote: t("reviews_card_6_quote"),
        author: t("reviews_card_6_author"),
        tag: t("reviews_tag_value"),
      },
    ],
    [t]
  );

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={1.2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              icon={<VerifiedRoundedIcon />}
              label={t("reviews_badge")}
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: "action.selected",
              }}
            />
            <Chip
              label={t("reviews_badge_premium")}
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 800 }}
            />
          </Stack>

          <Typography
            variant="h3"
            sx={{ fontWeight: 950, letterSpacing: -0.8, lineHeight: 1.05 }}
          >
            {t("reviews_title")}
          </Typography>

          <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
            {t("reviews_subtitle")}
          </Typography>

          {/* Summary row */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            alignItems={{ sm: "center" }}
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Rating value={4.9} precision={0.1} readOnly />
              <Typography sx={{ fontWeight: 900 }}>
                {t("reviews_rating")}
              </Typography>
            </Stack>

            <Divider
              flexItem
              orientation="vertical"
              sx={{ display: { xs: "none", sm: "block" } }}
            />

            <Typography color="text.secondary">{t("reviews_hint")}</Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={t("reviews_tag_families")}
                variant="outlined"
                sx={{ borderRadius: 999 }}
              />
              <Chip
                label={t("reviews_tag_couples")}
                variant="outlined"
                sx={{ borderRadius: 999 }}
              />
              <Chip
                label={t("reviews_tag_groups")}
                variant="outlined"
                sx={{ borderRadius: 999 }}
              />
            </Stack>
          </Stack>
        </Stack>

        {/* Cards (CSS Grid instead of MUI Grid) */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          }}
        >
          {reviews.map((r, i) => (
            <Card
              key={i}
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 2.4 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormatQuoteRoundedIcon sx={{ opacity: 0.55 }} />
                    {r.tag ? (
                      <Chip
                        label={r.tag}
                        size="small"
                        sx={{
                          borderRadius: 999,
                          fontWeight: 800,
                          bgcolor: "action.selected",
                        }}
                      />
                    ) : null}
                  </Stack>

                  <Typography sx={{ fontWeight: 950, letterSpacing: -0.3 }}>
                    {r.title}
                  </Typography>

                  <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    “{r.quote}”
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    — {r.author}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Bottom note */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("reviews_footer_note")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
